import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  CardActions,
  Button,
  Container,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { ScholarshipListItem } from '../types';
import { useNavigate } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { database } from '../firebase';
import { useScholarships } from '../contexts/ScholarshipsContext';

interface HeadCell {
  id: keyof ScholarshipListItem;
  label: string;
  sortable: boolean;
  align?: 'left' | 'center' | 'right';
  minWidth?: number;
}

const headCells: HeadCell[] = [
  { id: 'name', label: 'Scholarship', sortable: true, minWidth: 180 },
  { id: 'sponsor', label: 'Sponsor', sortable: true, minWidth: 300 },
  { id: 'description', label: 'Description', sortable: true },
  { id: 'deadline', label: 'Deadline', sortable: true, align: 'right' },
  { id: 'postedDate', label: 'Posted Date', sortable: true, align: 'right' },
  { id: 'eligibility', label: 'Eligibility Criteria', sortable: false, align: 'center' },
  { id: 'amount', label: 'Amount', sortable: true, align: 'right' },
];

type Order = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear().toString().slice(-2)}`;
};

const Scholarships: React.FC = () => {
  const [view, setView] = useState<'list' | 'card'>('list');
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof ScholarshipListItem>('name');
  const [search, setSearch] = useState({
    sponsor: '',
    deadline: '',
    postedDate: '',
    eligibility: '',
  });
  const { scholarships, loading, error } = useScholarships();
  const navigate = useNavigate();

  // Map Scholarship[] from context to ScholarshipListItem[] for UI
  const scholarshipsList: ScholarshipListItem[] = scholarships.map(s => ({
    id: s.id,
    name: s.name,
    sponsor: s.schoolName || '',
    description: s.description || '',
    deadline: s.deadline || '',
    postedDate: '', // Not available in schema
    eligibility: s.eligibility || '', // Placeholder, update as needed
    amount: s.amount || '',
    schoolId: s.schoolId || '',
  }));

  const handleRequestSort = (
    property: keyof ScholarshipListItem
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleViewChange = (
    _: React.MouseEvent<HTMLElement>,
    nextView: 'list' | 'card' | null
  ) => {
    if (nextView) setView(nextView);
  };

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearch({ ...search, [e.target.name]: e.target.value });
  };

  const filteredScholarships = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return scholarshipsList.filter((sch) => {
      // Deadline filter
      let show = false;
      if (!sch.deadline) {
        show = true;
      } else {
        const deadlineDate = new Date(sch.deadline);
        deadlineDate.setHours(0, 0, 0, 0);
        if (!isNaN(deadlineDate.getTime()) && deadlineDate >= today) {
          show = true;
        }
      }
      return (
        show &&
        sch.sponsor.toLowerCase().includes(search.sponsor.toLowerCase()) &&
        sch.deadline.toLowerCase().includes(search.deadline.toLowerCase()) &&
        sch.postedDate.toLowerCase().includes(search.postedDate.toLowerCase()) &&
        sch.eligibility.toLowerCase().includes(search.eligibility.toLowerCase())
      );
    });
  }, [search, scholarshipsList]);

  const sortedScholarships = useMemo(() => {
    if (headCells.find((h) => h.id === orderBy)?.sortable) {
      return [...filteredScholarships].sort(getComparator(order, orderBy));
    }
    return filteredScholarships;
  }, [filteredScholarships, order, orderBy]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Scholarships
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          <Box>
            <TextField
              label="Sponsor"
              name="sponsor"
              value={search.sponsor}
              onChange={handleSearchChange}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>
          <Box>
            <TextField
              label="Deadline"
              name="deadline"
              value={search.deadline}
              onChange={handleSearchChange}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>
          <Box>
            <TextField
              label="Posted Date"
              name="postedDate"
              value={search.postedDate}
              onChange={handleSearchChange}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>
          <Box>
            <TextField
              label="Eligibility Criteria"
              name="eligibility"
              value={search.eligibility}
              onChange={handleSearchChange}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>
        </Box>
      </Paper>
      <Box display="flex" alignItems="center" mb={2}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          {sortedScholarships.length.toLocaleString()} scholarships displayed
        </Typography>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={handleViewChange}
          size="small"
        >
          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon />
          </ToggleButton>
          <ToggleButton value="card" aria-label="card view">
            <ViewModuleIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {view === 'list' ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.align || 'left'}
                    sx={{ minWidth: headCell.minWidth, whiteSpace: 'nowrap' }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id)}
                      >
                        <strong>{headCell.label}</strong>
                      </TableSortLabel>
                    ) : (
                      <strong>{headCell.label}</strong>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedScholarships.map((sch) => (
                <TableRow key={sch.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/scholarships/${sch.id}`, { state: { schoolId: sch.schoolId } })}>
                  <TableCell>{sch.name}</TableCell>
                  <TableCell>{sch.sponsor}</TableCell>
                  <TableCell>{sch.description}</TableCell>
                  <TableCell align="right">{formatDate(sch.deadline)}</TableCell>
                  <TableCell align="right">{formatDate(sch.postedDate)}</TableCell>
                  <TableCell align="center" sx={{ textAlign: 'center' }}>
                    {sch.eligibility ? `${sch.eligibility}%` : ''}
                  </TableCell>
                  <TableCell align="right">
                    {sch.amount && !isNaN(Number(sch.amount)) ? `$${Number(sch.amount).toLocaleString()}` : sch.amount}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          {sortedScholarships.map((sch) => (
            <Box key={sch.id} sx={{ border: '1px solid #eee', borderRadius: 2, p: 2, bgcolor: 'background.paper', boxShadow: 1 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {sch.name}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Sponsor: {sch.sponsor}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {sch.description}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Deadline:</strong> {sch.deadline}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Posted:</strong> {sch.postedDate}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Eligibility:</strong> {sch.eligibility}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Amount:</strong> {sch.amount}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => navigate(`/scholarships/${sch.id}`, { state: { schoolId: sch.schoolId } })}>View Details</Button>
                </CardActions>
              </Card>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Scholarships; 